<template>
  <div
    ref="modal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="staticBackdropLabel"
    aria-hidden="true"
  >
    <div
      class="
        modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable
      "
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ $t("incomingProducts.createDeliveryLabel") }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            :aria-label="$t(`modal.close`)"
            v-on:click="$emit('update:show', false)"
          ></button>
        </div>
        <div class="modal-body">
          <div class="text-start m-2 custom-input">
            <div class="mb-3 whitebg-input" v-if="mode === 'method'">
              <label class="form-label bold-12 text-gray">{{
                $t("incomingProducts.deliveryMethod")
              }}</label>
              <Skeletor
                height="44"
                v-if="loadingMethods && deliveryMethods.length === 0"
                class="rounded-box mb-2"
              />
              <div class="wd-method-list" v-if="availableMethods.length > 0">
                <label
                  class="wd-method-item"
                  v-for="method in availableMethods"
                  :key="method.id"
                  :class="{ selected: selectedMethod === method.id }"
                >
                  <input
                    type="radio"
                    name="wd-delivery-method"
                    :value="method.id"
                    v-model="selectedMethod"
                  />
                  <span class="wd-method-check"><i></i></span>
                  <img
                    v-if="methodLogo(method)"
                    :src="methodLogo(method)"
                    class="wd-method-logo"
                    alt=""
                  />
                  <span class="flex-grow-1 min-w-0">
                    <span class="d-block bold-14 text-truncate">
                      {{ method.name }}
                      <span
                        class="badge bg-blue-lt ms-1"
                        v-if="method.is_automatic_select"
                        >{{ $t("incomingProducts.autoSelect") }}</span
                      >
                    </span>
                    <span
                      class="d-block regular-12 text-gray text-truncate"
                      v-if="method.courier_name"
                      >{{ method.courier_name }}</span
                    >
                  </span>
                  <span
                    class="badge bg-secondary-lt text-nowrap"
                    v-if="methodTypeLabel(method.type)"
                    >{{ methodTypeLabel(method.type) }}</span
                  >
                </label>
              </div>
              <div class="regular-12 text-gray" v-else-if="!loadingMethods">
                {{ $t("incomingProducts.noDeliveryMethods") }}
              </div>
            </div>
            <div class="mb-3 whitebg-input" v-if="mode === 'manual'">
              <label class="form-label bold-12 text-gray">{{
                $t("incomingProducts.insertTrackingCode")
              }}</label>
              <input
                type="text"
                class="form-control mb-3"
                v-model="tracking"
                :placeholder="$t(`incomingProducts.insertTrackingCode`)"
              />
              <v-select
                v-model:options="couriers"
                v-on:search="searchCourier"
                :filterable="false"
                v-model="courier"
                label="name"
                :placeholder="$t(`incomingProducts.searchForACourier`)"
                append-to-body
              >
                <template v-slot:option="option">
                  <img :src="option.logo" alt="" class="w-3rem me-3" />
                  <span>{{ option.name }}</span>
                </template>
                <template v-slot:selected-option="option">
                  <img :src="option.logo" alt="" class="w-3rem me-3" />
                  <span>{{ option.name }}</span>
                </template>
              </v-select>
            </div>
            <div class="mb-3 whitebg-input" v-if="needsAddress">
              <label class="form-label bold-12 text-gray">{{
                operationType === 2
                  ? $t("incomingProducts.pickupAddress")
                  : $t("incomingProducts.deliveryAddress")
              }}</label>
              <Skeletor
                height="20"
                v-if="loadingAddresses"
                class="rounded-box mb-2"
              />
              <div
                class="
                  form-selectgroup form-selectgroup-boxes
                  d-flex
                  flex-column
                "
                v-if="savedAddresses.length > 0"
              >
                <label
                  class="form-selectgroup-item flex-fill"
                  v-for="address in savedAddresses"
                  :key="address.id"
                >
                  <input
                    type="radio"
                    name="wd-address"
                    :value="address.id"
                    class="form-selectgroup-input"
                    v-model="addressMode"
                  />
                  <div
                    class="form-selectgroup-label d-flex align-items-center p-3"
                  >
                    <div class="me-3">
                      <span class="form-selectgroup-check"></span>
                    </div>
                    <div>{{ addressLabel(address) }}</div>
                  </div>
                </label>
                <label class="form-selectgroup-item flex-fill">
                  <input
                    type="radio"
                    name="wd-address"
                    value="new"
                    class="form-selectgroup-input"
                    v-model="addressMode"
                  />
                  <div
                    class="form-selectgroup-label d-flex align-items-center p-3"
                  >
                    <div class="me-3">
                      <span class="form-selectgroup-check"></span>
                    </div>
                    <div>{{ $t("incomingProducts.addNewAddress") }}</div>
                  </div>
                </label>
              </div>
              <div class="mt-3" v-if="addressMode === 'new'">
                <div class="row">
                  <div class="col-6 mb-3">
                    <label class="form-label bold-12 text-gray">{{
                      $t("address.country")
                    }}</label>
                    <v-select
                      :options="countries"
                      v-model="newAddress.country_id"
                      label="name"
                      :reduce="(c) => c.id"
                      :placeholder="$t(`address.selectCountry`)"
                      append-to-body
                    />
                  </div>
                  <div class="col-6 mb-3">
                    <label class="form-label bold-12 text-gray">{{
                      $t("address.postCode")
                    }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="newAddress.post_code"
                    />
                  </div>
                  <div class="col-6 mb-3">
                    <label class="form-label bold-12 text-gray">{{
                      $t("address.city")
                    }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="newAddress.city"
                    />
                  </div>
                  <div class="col-6 mb-3">
                    <label class="form-label bold-12 text-gray">{{
                      $t("address.street")
                    }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="newAddress.street"
                    />
                  </div>
                  <div class="col-6 mb-3">
                    <label class="form-label bold-12 text-gray">{{
                      $t("address.house")
                    }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="newAddress.house"
                    />
                  </div>
                </div>
                <label class="cstm-check">
                  <input type="checkbox" v-model="saveAddress" />
                  <span class="text-black mx-2 regular-14">{{
                    $t("incomingProducts.saveAddressForLater")
                  }}</span>
                </label>
              </div>
            </div>
            <div class="mb-3 whitebg-input">
              <label class="form-label bold-12 text-gray">{{
                $t("incomingProducts.packagesNumber")
              }}</label>
              <input
                type="number"
                min="1"
                class="form-control"
                v-model="packages"
              />
            </div>
            <div class="alert alert-danger" v-if="error">{{ error }}</div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-link link-secondary"
            data-bs-dismiss="modal"
            v-on:click="$emit('update:show', false)"
          >
            {{ $t("modal.close") }}
          </button>
          <button
            class="btn btn-primary"
            v-on:click="submit()"
            :disabled="loading"
            :class="{ 'loading-spin': loading }"
          >
            {{ $t("finance.save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../modules/http";
import tenant from "../../modules/tenant";

const bootstrap = require("bootstrap");

const deliveryTypeLabels = {
  1: "typeNormal",
  3: "typeReturn",
  5: "typeNextDay",
  7: "typeFlexibleDay",
  8: "typeTimeslot",
  9: "typeDropOffReturn",
};

export default {
  name: "WarehouseDeliveryModal",
  props: {
    show: Boolean,
    mode: {
      type: String,
      default: "method",
    },
    id: {
      default() {
        return null;
      },
    },
    operationType: {
      default() {
        return 0;
      },
    },
    partner: {
      default() {
        return null;
      },
    },
    warehouseIncome: {
      default() {
        return null;
      },
    },
    warehouseOutcome: {
      default() {
        return null;
      },
    },
  },
  emits: ["update:show", "created"],
  data() {
    return {
      myModal: null,
      loading: false,
      loadingMethods: false,
      loadingAddresses: false,
      deliveryMethods: [],
      selectedMethod: null,
      savedAddresses: [],
      addressMode: null,
      newAddress: {
        country_id: null,
        post_code: "",
        city: "",
        street: "",
        house: "",
      },
      saveAddress: false,
      countries: [],
      packages: 1,
      tracking: "",
      courier: null,
      couriers: [],
      lastSearchId: 0,
      error: null,
    };
  },
  computed: {
    isIncome() {
      return this.operationType === 2;
    },
    isOutcome() {
      return this.operationType === 3;
    },
    needsAddress() {
      if (this.mode === "manual") {
        return false;
      }
      return this.isIncome || this.isOutcome;
    },
    storageUrl() {
      return tenant.getAppFilesUrl();
    },
    filterCountryCode() {
      if (this.needsAddress) {
        if (this.addressMode === "new") {
          return (
            this.countries.find((e) => e.id === this.newAddress.country_id)
              ?.code ?? null
          );
        }
        return (
          this.savedAddresses.find((e) => e.id === this.addressMode)
            ?.country_code ?? null
        );
      }
      return this.countryCodeById(this.warehouseIncome?.country_id);
    },
    availableMethods() {
      let methods;
      if (this.isIncome) {
        methods = this.deliveryMethods.filter((e) => e.is_return);
      } else {
        methods = this.deliveryMethods.filter(
          (e) => e.type === 1 || e.type === 5 || e.type === 7 || e.type === 8
        );
      }
      const code = this.filterCountryCode;
      if (code) {
        methods = methods.filter(
          (e) =>
            !e.supported_coutries || e.supported_coutries.indexOf(code) > -1
        );
      }
      return methods;
    },
  },
  mounted() {
    this.myModal = new bootstrap.Modal(this.$refs.modal, {
      backdrop: "static",
    });
  },
  watch: {
    show() {
      if (this.show) {
        this.reset();
        this.myModal.show();
        this.loadData();
      } else {
        this.myModal.hide();
      }
    },
    filterCountryCode() {
      if (
        this.selectedMethod !== null &&
        !this.availableMethods.find((e) => e.id === this.selectedMethod)
      ) {
        this.selectedMethod = null;
      }
    },
  },
  methods: {
    countryCodeById(countryId) {
      if (!countryId) {
        return null;
      }
      return this.countries.find((e) => e.id === countryId)?.code ?? null;
    },
    methodLogo(method) {
      let logo = method.icon ?? method.logo;
      if (!logo) {
        return null;
      }
      if (logo.indexOf("http") !== 0 && logo.indexOf("//") !== 0) {
        logo = this.storageUrl + "/" + logo;
      }
      return logo;
    },
    methodTypeLabel(type) {
      const key = deliveryTypeLabels[type];
      return key ? this.$t(`incomingProducts.${key}`) : null;
    },
    reset() {
      this.selectedMethod = null;
      this.packages = 1;
      this.tracking = "";
      this.courier = null;
      this.saveAddress = false;
      this.error = null;
      this.addressMode = null;
      this.newAddress = {
        country_id: null,
        post_code: "",
        city: "",
        street: "",
        house: "",
      };
    },
    loadData() {
      if (this.mode === "method" && this.deliveryMethods.length === 0) {
        this.loadingMethods = true;
        http
          .fetch("/deliveries/methods")
          .then((data) => {
            this.deliveryMethods = data;
            this.loadingMethods = false;
          })
          .catch(() => {
            this.loadingMethods = false;
          });
      }
      if (this.countries.length === 0) {
        http.fetch("/countries/all").then((data) => {
          this.countries = data;
        });
      }
      if (this.needsAddress) {
        this.loadAddresses();
      }
    },
    loadAddresses() {
      if (!this.partner?.id) {
        this.addressMode = "new";
        return;
      }
      this.loadingAddresses = true;
      http
        .fetch(`/crm/partners/${this.partner.id}/addresses`)
        .then((data) => {
          this.savedAddresses = data.addresses ?? [];
          if (this.savedAddresses.length > 0) {
            this.addressMode =
              this.savedAddresses.find((e) => e.default)?.id ??
              this.savedAddresses[0].id;
          } else {
            this.addressMode = "new";
          }
          this.loadingAddresses = false;
        })
        .catch(() => {
          this.addressMode = "new";
          this.loadingAddresses = false;
        });
    },
    addressLabel(address) {
      return `${address.post_code ?? ""} ${address.city ?? ""}, ${
        address.street ?? ""
      } ${address.house ?? ""} (${address.country_name ?? ""})`;
    },
    searchCourier(search, loading) {
      if (search.length < 2) {
        return;
      }
      loading(true);
      let lastSearchId = Math.random();
      this.lastSearchId = lastSearchId;
      http
        .fetch(`/deliveries/search-courier`, { q: search })
        .then((data) => {
          if (this.lastSearchId === lastSearchId) {
            this.couriers = data.data;
            loading(false);
          }
        })
        .catch(() => {
          loading(false);
        });
    },
    validate() {
      if (this.mode === "method" && !this.selectedMethod) {
        this.error = this.$t("incomingProducts.selectDeliveryMethod");
        return false;
      }
      if (this.mode === "manual" && (this.tracking?.length ?? 0) < 3) {
        this.error = this.$t("incomingProducts.insertTrackingCode");
        return false;
      }
      if (
        this.needsAddress &&
        this.addressMode === "new" &&
        ((this.newAddress.post_code?.length ?? 0) < 1 ||
          (this.newAddress.city?.length ?? 0) < 1 ||
          (this.newAddress.street?.length ?? 0) < 1)
      ) {
        this.error = this.$t("incomingProducts.addressRequired");
        return false;
      }
      if (this.packages === null || this.packages < 1) {
        this.error = this.$t("incomingProducts.packagesNumber");
        return false;
      }
      this.error = null;
      return true;
    },
    buildAddress() {
      if (!this.needsAddress) {
        return null;
      }
      if (this.addressMode === "new") {
        return {
          country_id: this.newAddress.country_id,
          post_code: this.newAddress.post_code,
          city: this.newAddress.city,
          street: this.newAddress.street,
          house: this.newAddress.house,
        };
      }
      const address = this.savedAddresses.find(
        (e) => e.id === this.addressMode
      );
      if (!address) {
        return null;
      }
      return {
        country_id: address.country_id,
        post_code: address.post_code,
        city: address.city,
        street: address.street,
        house: address.house,
      };
    },
    submit() {
      if (!this.validate()) {
        return;
      }
      this.loading = true;
      http
        .fetch(
          "/warehouses/outgoing/" + this.id + "/delivery",
          {
            tracking: this.mode === "manual" ? this.tracking : null,
            method: this.mode === "method" ? this.selectedMethod : null,
            packages: this.packages,
            auto_delivery: this.mode === "default" ? 1 : 0,
            courier: this.mode === "manual" ? this.courier?.id ?? "" : "",
            address: this.buildAddress(),
            save_address:
              this.needsAddress &&
              this.addressMode === "new" &&
              this.saveAddress
                ? 1
                : null,
          },
          true
        )
        .then((data) => {
          this.loading = false;
          this.$emit("update:show", false);
          this.$emit("created", {
            data: data,
            methodId: this.mode === "method" ? this.selectedMethod : null,
            automatic: this.mode !== "manual",
          });
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.wd-method-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 18rem;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.wd-method-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.9rem;
  background: #fff;
  border: 1px solid #e5e9ed;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.wd-method-item:hover {
  border-color: #a947d6;
}

.wd-method-item.selected {
  border-color: #a947d6;
  box-shadow: inset 0 0 0 1px #a947d6;
  background: #faf5fd;
}

.wd-method-item input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.wd-method-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  flex: 0 0 auto;
  border: 1px solid #cdcedd;
  border-radius: 50%;
  background: #fff;
}

.wd-method-item.selected .wd-method-check {
  border-color: #a947d6;
}

.wd-method-item.selected .wd-method-check i {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: #a947d6;
}

.wd-method-logo {
  height: 1.4rem;
  max-width: 3.2rem;
  object-fit: contain;
  flex: 0 0 auto;
}

.min-w-0 {
  min-width: 0;
}
</style>
